import { LEVELS_CONFIG } from './constants';
import type { Level, LevelsConfig, LogFnParams, LoggerConfig, MethodFn, TransportFn } from './types';

import type { SealedEvent } from 'ts-typed-events';
import { createEmitter } from 'ts-typed-events';

export class Logger {
    #level: Level;
    #levelsConfig: LevelsConfig;
    #transports: TransportFn[];

    private emitters: {
        onLevelChange: ReturnType<typeof createEmitter<Level>>;
    };

    events: {
        onLevelChange: SealedEvent<Level>;
    };

    constructor(config: LoggerConfig | undefined = {}) {
        const { level = 'info', transports = [], levelsConfig = LEVELS_CONFIG } = config;
        this.#level = level;
        this.#transports = transports;
        this.#levelsConfig = levelsConfig;
        this.emitters = {
            onLevelChange: createEmitter<Level>(),
        };
        this.events = {
            onLevelChange: this.emitters.onLevelChange.event,
        };
    }

    get level(): Level {
        return this.#level;
    }

    set level(value: Level) {
        this.#level = value;
        this.emitters.onLevelChange.emit(value);
    }

    get levelsConfig(): LevelsConfig {
        return this.#levelsConfig;
    }

    set levelsConfig(value: LevelsConfig) {
        this.#levelsConfig = value;
    }

    get transports(): TransportFn[] {
        return this.#transports;
    }

    set transports(value: TransportFn[]) {
        this.#transports = value;
    }

    private log = <L>(options: LogFnParams<L>) => {
        const { value: levelValue } = this.levelsConfig[options.level];
        const isLevelEnabled = levelValue >= this.levelsConfig[this.level].value;
        this.transports.forEach((transport) => {
            transport({ ...options, isLevelEnabled, levelsConfig: this.levelsConfig });
        });
    };

    fatal: MethodFn<'fatal'> = (error, ...args: unknown[]) => {
        this.log<'fatal'>({ level: 'fatal', error, methodFnArgs: [...args] });
    };

    error: MethodFn<'error'> = (error, ...args: unknown[]) => {
        this.log<'error'>({ level: 'error', error, methodFnArgs: [...args] });
    };

    warn: MethodFn<'warn'> = (...args: unknown[]) => {
        this.log<'warn'>({ level: 'warn', methodFnArgs: [...args] });
    };

    info: MethodFn<'info'> = (...args: unknown[]) => {
        this.log<'info'>({ level: 'info', methodFnArgs: [...args] });
    };

    debug: MethodFn<'debug'> = (...args: unknown[]) => {
        this.log<'debug'>({ level: 'debug', methodFnArgs: [...args] });
    };

    trace: MethodFn<'trace'> = (...args: unknown[]) => {
        this.log<'trace'>({ level: 'trace', methodFnArgs: [...args] });
    };
}
